import { MetricResult } from '../../../../api/types';
import { MetricId } from '../../../../api/types-graphql';
import { toTimeSegmentLabel } from '../../../../api/utils';
import { useMetricDetailsMapContext } from '../../../../context/contexts';
import { MetricIdTypeArrays } from '../../../../types';
import { TitleConfig } from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import {
  useChartTitleForOverTimeChart,
  useMetricDisplayValues,
  useMetricQueries,
  useTitleConfigWithSubtitle,
} from '../common/hooks';
import {
  DataOrError,
  ExternalChartComponentProps,
  MetricQueryConfig,
} from '../common/types';
import { getOvertimeBenchmarkTimeSelection } from '../common/utils';
import {
  OverTimeUnsegmentedVizConfig,
  OverTimeUnsegmentedVizData,
  OverTimeUnsegmentedVizDataResponse,
  OverTimeUnsegmentedVizDataResponseForMetric,
  OverTimeUnsegmentedVizDataResponseForMetricWithoutBenchmark,
} from './types';

type ChartTitleOverTimeProps = {
  config: OverTimeUnsegmentedVizConfig;
  data: OverTimeUnsegmentedVizDataResponse | null;
  externalProps: ExternalChartComponentProps;
};

const useChartTitleOverTime = (props: ChartTitleOverTimeProps): TitleConfig => {
  const { config, data, externalProps } = props;
  const { queries } = config;
  const metrics = queries.map((q) => q.metric);
  const filters = queries.map((q) => q.filters);
  return useChartTitleForOverTimeChart({
    externalProps: props.externalProps,
    // TODO: Add runtime validation for this, and also
    // improve the types so this doesn't need to be done
    metrics: metrics as MetricIdTypeArrays,
    filters: '',
    timeSelection: config.timeSelection,
    metricsSql: data?.responseForMetrics.map((r) => r.metricSql) ?? [],
    segmentations: [],
    titleFromConfig: config.title,
  });
};

type QueriesConfig = Pick<
  OverTimeUnsegmentedVizConfig,
  'queries' | 'timeSelection'
>;

const useOverTimeUnsegmentedQueriesConfigToData = (
  config: QueriesConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<{
  responseForMetrics: OverTimeUnsegmentedVizDataResponseForMetricWithoutBenchmark[];
}> => {
  const { timeSliderState, segmentationState, filtersState } = externalProps;
  const { queries, timeSelection } = config;
  const mainQueries = queries.map((query) => ({
    metrics: [query.metric],
    timeSelection: timeSelection,
    filters: query.filters,
  }));

  const { queryResponses } = useMetricQueries({
    queries: mainQueries as MetricQueryConfig[],
    timeSliderState,
    segmentationState,
    filtersState,
  });
  const mainQueryResponses = queryResponses.slice(0, mainQueries.length);
  const firstError = queryResponses.find((r) => r.error)?.error;
  if (firstError) {
    return { data: null, error: firstError };
  }
  // We use firstMetricResults as each query here has only one metric
  const mainFirstMetricResults = mainQueryResponses.map(
    (r) => (r.data as MetricResult[])[0]
  );
  const { locale } = externalProps;

  let dataResponse: {
    responseForMetrics: OverTimeUnsegmentedVizDataResponseForMetricWithoutBenchmark[];
  };

  const responseForMetrics = mainFirstMetricResults.map((metricResult, i) => {
    const { metric } = queries[i];
    return {
      metric,
      metricSql: metricResult.meta.metricSql,
      data: metricResult.segments.map((segment) => {
        const dateLabel = toTimeSegmentLabel(
          segment.timeSegment,
          locale.selected
        );
        const mainGroupSegment = segment.groupSegments[0];
        const datapoint = {
          value: mainGroupSegment.data as number,
        };
        return {
          dateLabel,
          datapoint,
        };
      }),
    };
  });
  dataResponse = {
    responseForMetrics,
  };
  return {
    data: dataResponse,
    error: null,
  };
};

const combineMainResponseWithBenchmark = (
  // This assumes that the order of mainRes and benchmarkRes arrays
  // is the same, i.e. benchmarkData[i] will correspond to mainData[i]
  mainResponse: {
    responseForMetrics: OverTimeUnsegmentedVizDataResponseForMetricWithoutBenchmark[];
  },
  benchmarkResponse: {
    responseForMetrics: OverTimeUnsegmentedVizDataResponseForMetricWithoutBenchmark[];
  }
): OverTimeUnsegmentedVizDataResponse<true> => {
  // string is for date
  const metricToBenchmarkDataMap: Partial<
    Record<
      MetricId,
      OverTimeUnsegmentedVizDataResponseForMetricWithoutBenchmark
    >
  > = {};
  benchmarkResponse.responseForMetrics.forEach((r) => {
    metricToBenchmarkDataMap[r.metric.value] = r;
  });
  return {
    responseForMetrics: mainResponse.responseForMetrics.map(
      ({ metric, metricSql, data: mainData }) => {
        const benchmarkDataForMetric = metricToBenchmarkDataMap[metric.value];
        if (!benchmarkDataForMetric) {
          throw new Error(
            `Benchmark data not found corresponding to mainData for metric ${metric.value}`
          );
        }
        const resForMetric: OverTimeUnsegmentedVizDataResponseForMetric<true> =
          {
            metric,
            metricSql: `Main - ${metricSql}, Benchmark - ${benchmarkDataForMetric.metricSql}`,
            data: mainData.map((d, i) => {
              const benchmarkData = benchmarkDataForMetric.data[i];
              return {
                dateLabel: d.dateLabel,
                datapoint: {
                  hasBenchmark: true,
                  value: d.datapoint.value,
                  benchmarkData: {
                    value: benchmarkData.datapoint.value,
                    benchmarkDateLabel: benchmarkData.dateLabel,
                  },
                },
              };
            }),
          };
        return resForMetric;
      }
    ),
  };
};

const useOverTimeUnsegmentedConfigToDataResponse = (
  config: OverTimeUnsegmentedVizConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<OverTimeUnsegmentedVizDataResponse> => {
  const { queries, benchmarkConfig, timeSelection } = config;
  const mainQueriesConfig: QueriesConfig = {
    queries,
    timeSelection,
  };
  let benchmarkQueriesConfig: QueriesConfig = { queries: [], timeSelection };
  if (benchmarkConfig) {
    const benchmarkTimeSelection = getOvertimeBenchmarkTimeSelection(
      timeSelection,
      benchmarkConfig.benchmarkPeriod
    );
    benchmarkQueriesConfig = {
      queries,
      timeSelection: benchmarkTimeSelection,
    };
  }
  const mainData = useOverTimeUnsegmentedQueriesConfigToData(
    mainQueriesConfig,
    externalProps
  );
  const benchmarkData = useOverTimeUnsegmentedQueriesConfigToData(
    benchmarkQueriesConfig,
    externalProps
  );
  const error = mainData.error || benchmarkData.error;
  if (error) {
    return {
      data: null,
      error,
    };
  }
  if (!benchmarkConfig) {
    const responseForMetrics = mainData.data.responseForMetrics.map((r) => {
      const responseForMetric: OverTimeUnsegmentedVizDataResponseForMetric<false> =
        {
          metric: r.metric,
          metricSql: r.metricSql,
          data: r.data.map((d) => {
            return {
              dateLabel: d.dateLabel,
              datapoint: { value: d.datapoint.value, hasBenchmark: false },
            };
          }),
        };
      return responseForMetric;
    });
    return {
      data: {
        responseForMetrics,
      },
      error: mainData.error,
    };
  } else {
    const combinedResponse = combineMainResponseWithBenchmark(
      mainData.data,
      benchmarkData.data
    );
    return {
      data: combinedResponse,
      error: null,
    };
  }
};

export const useOverTimeConfigToData = (
  config: OverTimeUnsegmentedVizConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<OverTimeUnsegmentedVizData> => {
  const { data, error } = useOverTimeUnsegmentedConfigToDataResponse(
    config,
    externalProps
  );
  const { queries } = config;
  const metricDetailsMap = useMetricDetailsMapContext();
  const metricDisplayValues = useMetricDisplayValues(
    queries.map((q) => q.metric.value as unknown as MetricId)
  );
  const baseTitleConfig = useChartTitleOverTime({
    config,
    data,
    externalProps,
  });
  const titleConfig = useTitleConfigWithSubtitle(
    baseTitleConfig,
    config.subtitle
  );
  if (error) {
    return { data: null, error };
  }
  return {
    data: {
      dataResponse: data,
      titleConfig,
      metricDisplayValues,
      metricDetailsMap,
      chartColors: config.chartColors,
    },
    error: null,
  };
};
